import React from 'react';
import { toast } from 'react-toastify';
import authService from '../../../../services/authService';
import gamesService from '../../../../services/gamesService';
import storePasswordService, { type StorePasswordsResponse } from '../../../../services/storePasswordService';
import type { LottoBackfillMode, LottoCredentialsFormValues, LottoSetupEditMode, UseStoreDetailsOverviewLottoParams } from '../../../../types/features/storeDetails/overview';
import { formatUtcAssumedDateTimeMMDDYYYY } from '../../../../utils/formatUtils';
import { getLottoCredentialStatusDisplay } from '../utils/lottoCredentialStatus';
import { formatLastSync, formatSeenTimestamp } from '../utils/storeDetailsOverviewUtils';

const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const buildBackfillRequest = (days: number, forceFetch = false) => {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(end.getDate() - (days - 1));

    const request = {
        start_date: formatLocalDate(start),
    };

    return forceFetch ? { ...request, force_fetch: true } : request;
};

export const SCRATCHER_BULK_SYNC_DAYS = 365;
const STATE_LOTTERY_SERVICE = 'state-lottery';

export const useStoreDetailsOverviewLotto = ({
    store,
    onSilentStoreRefresh,
}: UseStoreDetailsOverviewLottoParams) => {
    const [lottoEnabled, setLottoEnabled] = React.useState<boolean>(store?.lotto_authorized ?? false);
    const [lottoDialogOpen, setLottoDialogOpen] = React.useState<boolean>(false);
    const [lottoEditMode, setLottoEditMode] = React.useState<LottoSetupEditMode>('retailer_id');
    const [lottoSaving, setLottoSaving] = React.useState<boolean>(false);
    const [lottoPasswordRevealing, setLottoPasswordRevealing] = React.useState<boolean>(false);
    const [lottoPasswordVisible, setLottoPasswordVisible] = React.useState<boolean>(false);
    const [lottoToggleUpdating, setLottoToggleUpdating] = React.useState<boolean>(false);
    const [enableLottoAfterCredentialsSave, setEnableLottoAfterCredentialsSave] = React.useState<boolean>(false);
    const [lottoForm, setLottoForm] = React.useState<LottoCredentialsFormValues>({
        username: '',
        password: '',
        lotteryRetailerId: '',
    });
    const [storePasswords, setStorePasswords] = React.useState<StorePasswordsResponse | null>(null);
    const passwordsRequestIdRef = React.useRef(0);
    const credentialsSaveIdRef = React.useRef(0);
    const [lottoBackfillDialogOpen, setLottoBackfillDialogOpen] = React.useState<boolean>(false);
    const [lottoBackfillDays, setLottoBackfillDays] = React.useState<string>('1');
    const [lottoBackfillMode, setLottoBackfillMode] = React.useState<LottoBackfillMode>('lotto');
    const [lottoBackfillLoading, setLottoBackfillLoading] = React.useState<boolean>(false);
    const [lottoBackfillForceFetch, setLottoBackfillForceFetch] = React.useState<boolean>(false);
    const isNcStore = React.useMemo(
        () => String(store?.state || '').trim().toUpperCase() === 'NC',
        [store?.state],
    );

    React.useEffect(() => {
        setLottoEnabled(store?.lotto_authorized ?? false);
    }, [store?.lotto_authorized]);

    // When switching stores, close the dialog and clear any in-progress draft state.
    React.useEffect(() => {
        setLottoDialogOpen(false);
        setLottoSaving(false);
        setLottoEditMode('retailer_id');
        setLottoPasswordVisible(false);
        setLottoPasswordRevealing(false);
        setEnableLottoAfterCredentialsSave(false);
        setLottoBackfillForceFetch(false);
        setLottoForm({ username: '', password: '', lotteryRetailerId: store?.lottery_reseller_id || '' });
    }, [store?.id]);

    React.useEffect(() => {
        if (!isNcStore && lottoBackfillForceFetch) {
            setLottoBackfillForceFetch(false);
        }
    }, [isNcStore, lottoBackfillForceFetch]);

    React.useEffect(() => {
        const requestId = ++passwordsRequestIdRef.current;
        credentialsSaveIdRef.current += 1;
        setStorePasswords(null);
        if (!store?.id) return;
        const loadStorePasswords = async () => {
            try {
                const data = await storePasswordService.getStorePasswords(store.id);
                if (requestId !== passwordsRequestIdRef.current) return;
                setStorePasswords(data);
            } catch (error) {
                if (requestId !== passwordsRequestIdRef.current) return;
                console.error('Error loading store passwords:', error);
                setStorePasswords(null);
            }
        };
        void loadStorePasswords();
        return () => {
            passwordsRequestIdRef.current += 1;
            credentialsSaveIdRef.current += 1;
        };
    }, [store?.id]);

    const lottoService = React.useMemo(
        () => storePasswords?.services?.find((service) => service.service === STATE_LOTTERY_SERVICE) ?? null,
        [storePasswords],
    );

    // This is the source-of-truth username we should display on the details card.
    const lottoCommittedUsername = React.useMemo(
        () => (typeof lottoService?.username === 'string' ? lottoService.username : ''),
        [lottoService?.username],
    );

    const lottoCredentialStatusDisplay = React.useMemo(
        () => getLottoCredentialStatusDisplay(lottoService?.status),
        [lottoService?.status],
    );

    const posService = React.useMemo(
        () =>
            storePasswords?.services?.find((service) => {
                const serviceName = (service.service || '').toLowerCase();
                return serviceName === 'pos' || serviceName.includes('pos');
            }) ?? null,
        [storePasswords],
    );

    const lottoLastSeenLabel = React.useMemo(
        () => formatSeenTimestamp(store?.lotto_seen) || formatLastSync(lottoService?.created_at, lottoService?.updated_at) || '—',
        [lottoService?.created_at, lottoService?.updated_at, store?.lotto_seen],
    );

    const hasLottoCredentials = React.useMemo(
        () => Boolean((lottoCommittedUsername || '').trim()),
        [lottoCommittedUsername],
    );

    React.useEffect(() => {
        // Keep the draft username in sync with the committed username when the dialog is closed.
        // This prevents unsaved edits from "sticking" across opens/cancels.
        if (lottoDialogOpen) return;
        setLottoForm((previous) => ({
            ...previous,
            username: lottoCommittedUsername,
            password: '',
            lotteryRetailerId: store?.lottery_reseller_id || '',
        }));
    }, [lottoCommittedUsername, lottoDialogOpen, store?.lottery_reseller_id]);

    const setLottoUsername = React.useCallback((username: string) => {
        setLottoForm((previous) => ({ ...previous, username }));
    }, []);

    const setLottoPassword = React.useCallback((password: string) => {
        setLottoForm((previous) => ({ ...previous, password }));
    }, []);

    const setLottoRetailerId = React.useCallback((lotteryRetailerId: string) => {
        setLottoForm((previous) => ({ ...previous, lotteryRetailerId }));
    }, []);

    const updateLottoAuthorizedStatus = React.useCallback(async (nextChecked: boolean) => {
        if (!store?.id || lottoToggleUpdating) {
            if (!store?.id) {
                toast.error('Store ID is missing');
            }
            return;
        }

        setLottoToggleUpdating(true);
        try {
            const updatedStore = await authService.updateStore(store.id, {
                lotto_authorized: nextChecked,
            });
            setLottoEnabled(updatedStore?.lotto_authorized ?? nextChecked);
            await Promise.resolve(onSilentStoreRefresh?.());
            toast.success(`Lotto setup ${nextChecked ? 'enabled' : 'disabled'} successfully`);
        } catch (error) {
            console.error('Error updating lotto setup status:', error);
            setLottoEnabled(store?.lotto_authorized ?? false);
            toast.error('Failed to update lotto setup status. Please try again.');
        } finally {
            setLottoToggleUpdating(false);
        }
    }, [lottoToggleUpdating, onSilentStoreRefresh, store?.id, store?.lotto_authorized]);

    const handleLottoEnabledChange = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextChecked = event.target.checked;

        if (nextChecked && !hasLottoCredentials) {
            setEnableLottoAfterCredentialsSave(true);
            setLottoEditMode('credentials');
            setLottoForm({
                username: lottoCommittedUsername,
                password: '',
                lotteryRetailerId: store?.lottery_reseller_id || '',
            });
            setLottoDialogOpen(true);
            return;
        }

        await updateLottoAuthorizedStatus(nextChecked);
    }, [hasLottoCredentials, lottoCommittedUsername, store?.lottery_reseller_id, updateLottoAuthorizedStatus]);

    const handleEditLottoPassword = React.useCallback(() => {
        setEnableLottoAfterCredentialsSave(false);
        setLottoEditMode('retailer_id');
        setLottoPasswordVisible(false);
        // Always open the dialog with a fresh draft seeded from committed values.
        setLottoForm({
            username: lottoCommittedUsername,
            password: '',
            lotteryRetailerId: store?.lottery_reseller_id || '',
        });
        setLottoDialogOpen(true);
    }, [lottoCommittedUsername, store?.lottery_reseller_id]);

    const handleRevealLottoPassword = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        if (lottoPasswordVisible) {
            setLottoPasswordVisible(false);
            return;
        }

        setLottoPasswordRevealing(true);
        try {
            const revealedCredential = await storePasswordService.revealStorePassword(
                store.id,
                STATE_LOTTERY_SERVICE,
            );
            setLottoForm((previous) => ({
                ...previous,
                username: revealedCredential.username || previous.username,
                password: revealedCredential.password || '',
            }));
            setLottoPasswordVisible(true);
        } catch (error) {
            console.error('Error revealing lotto password:', error);
            const message = error instanceof Error && error.message
                ? error.message
                : 'Failed to retrieve lotto password.';
            toast.error(message);
        } finally {
            setLottoPasswordRevealing(false);
        }
    }, [lottoPasswordVisible, store?.id]);

    const handleOpenLottoBackfill = React.useCallback(() => {
        setLottoBackfillMode('lotto');
        setLottoBackfillDays('1');
        setLottoBackfillForceFetch(false);
        setLottoBackfillDialogOpen(true);
    }, []);

    const handleOpenScratchersBackfill = React.useCallback(() => {
        setLottoBackfillMode('scratchers');
        setLottoBackfillDays(String(SCRATCHER_BULK_SYNC_DAYS));
        setLottoBackfillForceFetch(false);
        setLottoBackfillDialogOpen(true);
    }, []);

    const handleCloseLottoBackfill = React.useCallback(() => {
        if (lottoBackfillLoading) {
            return;
        }

        setLottoBackfillDialogOpen(false);
        setLottoBackfillForceFetch(false);
    }, [lottoBackfillLoading]);

    const handleLottoBackfillSubmit = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        const dayCount = Number(lottoBackfillDays);
        if (!Number.isInteger(dayCount) || dayCount <= 0) {
            toast.error('Enter a valid number of days to sync.');
            return;
        }

        setLottoBackfillLoading(true);

        try {
            const mode = lottoBackfillMode;
            const response = await gamesService.backfillLottoData(
                store.id,
                buildBackfillRequest(dayCount, mode === 'scratchers' && isNcStore && lottoBackfillForceFetch),
            );

            if (response.success) {
                setLottoBackfillDialogOpen(false);
                setLottoBackfillForceFetch(false);
                toast.success(
                    response.message ||
                    (mode === 'scratchers'
                        ? `Scratchers synced for ${dayCount} days`
                        : 'Lotto data sync started successfully'),
                );
                if (mode === 'scratchers') {
                    await Promise.resolve(onSilentStoreRefresh?.());
                }
                return;
            }

            toast.error(
                response.message ||
                (mode === 'scratchers'
                    ? 'Failed to sync scratcher data. Please try again.'
                    : 'Failed to sync lotto data. Please try again.'),
            );
        } catch (error) {
            console.error('Error syncing lotto data:', error);
            toast.error(
                lottoBackfillMode === 'scratchers'
                    ? 'Failed to sync scratcher data. Please try again.'
                    : 'Failed to sync lotto data. Please try again.',
            );
        } finally {
            setLottoBackfillLoading(false);
        }
    }, [isNcStore, lottoBackfillDays, lottoBackfillForceFetch, lottoBackfillMode, onSilentStoreRefresh, store?.id]);

    const handleSaveLottoCredentials = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        const nextUsername = lottoForm.username.trim();
        const nextPassword = lottoForm.password;

        if ((enableLottoAfterCredentialsSave || !hasLottoCredentials) && (!nextUsername || !nextPassword.trim())) {
            toast.error('Username and password are required');
            return;
        }

        if (!nextUsername || !nextPassword.trim()) {
            toast.error('Enter both username and password to update credentials');
            return;
        }

        const requestId = ++credentialsSaveIdRef.current;
        setLottoSaving(true);
        try {
            await storePasswordService.saveLottoPassword({
                store_id: store.id,
                username: nextUsername,
                password: nextPassword,
            });
            if (requestId !== credentialsSaveIdRef.current) return;
            // Preserve the initial metadata read until credentials are actually saved.
            passwordsRequestIdRef.current += 1;
            const refreshedStorePasswords = await storePasswordService.getStorePasswords(store.id);
            if (requestId !== credentialsSaveIdRef.current) return;
            setStorePasswords(refreshedStorePasswords);

            if (enableLottoAfterCredentialsSave) {
                const updatedStore = await authService.updateStore(store.id, {
                    lotto_authorized: true,
                });
                if (requestId !== credentialsSaveIdRef.current) return;
                setLottoEnabled(updatedStore?.lotto_authorized ?? true);
            }

            await Promise.resolve(onSilentStoreRefresh?.());
            if (requestId !== credentialsSaveIdRef.current) return;
            setEnableLottoAfterCredentialsSave(false);
            setLottoPasswordVisible(false);
            setLottoForm((previous) => ({ ...previous, password: '' }));
            toast.success('Lotto credentials saved successfully');
        } catch (error) {
            if (requestId !== credentialsSaveIdRef.current) return;
            console.error('Error saving lotto credentials:', error);
            setEnableLottoAfterCredentialsSave(false);
            setLottoEnabled(store?.lotto_authorized ?? false);
            const message = error instanceof Error && error.message
                ? error.message
                : 'Failed to save lotto credentials. Please try again.';
            toast.error(message);
        } finally {
            if (requestId === credentialsSaveIdRef.current) setLottoSaving(false);
        }
    }, [
        enableLottoAfterCredentialsSave,
        hasLottoCredentials,
        lottoForm.password,
        lottoForm.username,
        onSilentStoreRefresh,
        store?.id,
        store?.lotto_authorized,
    ]);

    const handleSaveLottoRetailerId = React.useCallback(async () => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        const nextRetailerId = lottoForm.lotteryRetailerId.trim();
        const currentRetailerId = (store.lottery_reseller_id || '').trim();
        if (nextRetailerId === currentRetailerId) {
            toast.info('Lottery retailer ID is unchanged');
            return;
        }

        setLottoSaving(true);
        try {
            await authService.updateStore(store.id, {
                lottery_reseller_id: nextRetailerId || null,
            });
            await Promise.resolve(onSilentStoreRefresh?.());
            toast.success('Lottery retailer ID saved successfully');
        } catch (error) {
            console.error('Error saving lottery retailer ID:', error);
            const message = error instanceof Error && error.message
                ? error.message
                : 'Failed to save lottery retailer ID. Please try again.';
            toast.error(message);
        } finally {
            setLottoSaving(false);
        }
    }, [lottoForm.lotteryRetailerId, onSilentStoreRefresh, store?.id, store?.lottery_reseller_id]);

    const handleSaveLottoSetup = React.useCallback(async () => {
        if (lottoEditMode === 'credentials') {
            await handleSaveLottoCredentials();
            return;
        }
        await handleSaveLottoRetailerId();
    }, [handleSaveLottoCredentials, handleSaveLottoRetailerId, lottoEditMode]);

    const handleCloseLottoDialog = React.useCallback(() => {
        setLottoDialogOpen(false);
        setLottoEditMode('retailer_id');
        setLottoPasswordVisible(false);
        setLottoPasswordRevealing(false);
        setEnableLottoAfterCredentialsSave(false);
        setLottoEnabled(store?.lotto_authorized ?? false);
        // Drop any unsaved draft values.
        setLottoForm({
            username: lottoCommittedUsername,
            password: '',
            lotteryRetailerId: store?.lottery_reseller_id || '',
        });
    }, [lottoCommittedUsername, store?.lottery_reseller_id, store?.lotto_authorized]);

    return {
        hasLottoCredentials,
        handleCloseLottoDialog,
        handleEditLottoPassword,
        handleCloseLottoBackfill,
        handleLottoEnabledChange,
        handleLottoBackfillSubmit,
        handleOpenLottoBackfill,
        handleScratchersBulkSync: handleOpenScratchersBackfill,
        handleSaveLottoCredentials,
        handleSaveLottoRetailerId,
        handleSaveLottoSetup,
        handleRevealLottoPassword,
        lottoBackfillDays,
        lottoBackfillDialogOpen,
        lottoBackfillForceFetch,
        lottoBackfillLoading,
        lottoBackfillMode,
        lottoBackfillShowForceFetch: isNcStore,
        scratcherBulkSyncDays: SCRATCHER_BULK_SYNC_DAYS,
        scratcherBulkSyncLoading: lottoBackfillLoading && lottoBackfillMode === 'scratchers',
        lottoDialogOpen,
        lottoEditMode,
        lottoEnabled,
        lottoCredentialStatusDisplay,
        lottoCommittedUsername,
        lottoForm,
        lottoLastSeenLabel,
        lottoPasswordLastRotatedLabel: formatUtcAssumedDateTimeMMDDYYYY(lottoService?.rotated_at, { fallback: '-' }),
        lottoPasswordRevealing,
        lottoPasswordVisible,
        lottoSaving,
        posService,
        setLottoBackfillDays,
        setLottoBackfillForceFetch,
        setLottoEditMode,
        setLottoPassword,
        setLottoRetailerId,
        setLottoUsername,
    };
};
